import { IVectorLayerGroupConfig } from "esdm-map/dist/layers/groups/vector/interfaces/IVectorLayerGroupConfig";
import { WfsParamSourceMapServer, IWfsParamSource } from "esdm-map/dist/layers/vector/sources/wfs";
import { VectorLayerStrategy } from "esdm-map/dist/layers/vector/VectorLayerStrategy";

import { BuckinghamshireRecordType, BuckinghamshireRecordSubType} from "./BuckinghamshireRecordTypes";
import { IWmsLayerGroupConfig } from "esdm-map/dist/layers/groups/wms/interfaces/IWmsLayerGroupConfig";
import { WfsBboxAsWktRequestParams } from "esdm-map/dist/layers/vector/sources/wfs/WfsBboxAsWktRequestParams";

export class DefaultLayerConfigurationFactory {

    private mapFileUrl: string = "/api/MapServer/Hbsmr";

    private paramSource(): IWfsParamSource {
        return new WfsParamSourceMapServer(new WfsBboxAsWktRequestParams(),{srsName: "EPSG:27700"});
    }

    constructor() {
    }

    public layerConfig = (
        recordType: BuckinghamshireRecordType,
        recordSubType: BuckinghamshireRecordSubType = null,
        opts: IVectorLayerGroupConfig = {}
    ): IVectorLayerGroupConfig | IWmsLayerGroupConfig => {
        let layerConfig: IVectorLayerGroupConfig | IWmsLayerGroupConfig = null;

        switch (recordType) {
            case BuckinghamshireRecordType.Monument:
                layerConfig = this.Monument(opts);
                break;

            case BuckinghamshireRecordType.Event:
                layerConfig = this.Event(opts);
                break;

            case BuckinghamshireRecordType.Consultation:
                layerConfig = this.Consultation(opts);
                break;                       

            case BuckinghamshireRecordType.Hlc_1820:
                layerConfig = this.Hlc1820();
                break;

            case BuckinghamshireRecordType.Hlc_1880:
                layerConfig = this.Hlc1880();
                break;

            case BuckinghamshireRecordType.Hlc_2000:
                layerConfig = this.Hlc2000();
                break;

            case BuckinghamshireRecordType.HistoricRidgeAndFurrow:
                layerConfig = this.HistoricRidgeAndFurrow();
                break;
        }

        if (layerConfig === null && recordSubType !== null) {
            switch (recordSubType) {

                case BuckinghamshireRecordSubType.ConservationArea:
                    layerConfig = this.ConservationArea(opts);
                    break; 

                case BuckinghamshireRecordSubType.ScheduledMonument:
                    layerConfig = this.ScheduledMonument(opts);
                    break;

                case BuckinghamshireRecordSubType.RegisteredParksAndGardens:
                    layerConfig = this.RegisteredParksAndGardens(opts);
                    break; 

                case BuckinghamshireRecordSubType.ArchaeologicalNotificationAreas:
                    layerConfig = this.ArchaeologicalNotificationAreas(opts);
                    break; 

                case BuckinghamshireRecordSubType.ListedBuilding:
                    layerConfig = this.ListedBuilding(opts);
                    break;
                
                case BuckinghamshireRecordSubType.LocallyListedBuilding:
                    layerConfig = this.LocallyListedBuilding(opts);
                    break;
            }
        }

        return layerConfig;
    }

    private HistoricRidgeAndFurrow = (): IWmsLayerGroupConfig => {
        return {
            displayName: "Historic ridge and furrow",
            ref: "HRAF",
            type: BuckinghamshireRecordType.HistoricRidgeAndFurrow,
            url: "/api/ogcservice/BucksHistoric",
            displayInLayerSwitcher: true,
            displayInLegend: false,
            visibleOnFirstLoad: false,
            queryable: false,               
            layers: [
                {
                    layerName: "1",
                    minZoom: 1,
                    maxZoom: 19
                }
            ]
        }
    }

    private Hlc1820 = (): IWmsLayerGroupConfig => {
        return {
            displayName: "HLC 1820",
            ref: "HLC1820",
            type: BuckinghamshireRecordType.Hlc_1820,
            url: "/api/ogcservice/BucksUbp",
            displayInLayerSwitcher: true,
            displayInLegend: false,
            visibleOnFirstLoad: false,
            queryable: false,
            info_format: "text/html",
            layers: [
                {
                    layerName: "5",
                    minZoom: 1,
                    maxZoom: 19
                }
            ]
        }
    }

    private Hlc1880 = (): IWmsLayerGroupConfig => {
        return {
            displayName: "HLC 1880",
            ref: "HLC1880",
            type: BuckinghamshireRecordType.Hlc_1880,
            url: "/api/ogcservice/BucksUbp",
            displayInLayerSwitcher: true,
            displayInLegend: false,
            visibleOnFirstLoad: false,
            queryable: false,
            info_format: "text/html",
            layers: [
                {
                    layerName: "6",
                    minZoom: 1,
                    maxZoom: 19
                }
            ]
        }
    }

    private Hlc2000 = (): IWmsLayerGroupConfig => {
        return {
            displayName: "HLC 2000",
            ref: "HLC2000",
            type: BuckinghamshireRecordType.Hlc_2000,
            url: "/api/ogcservice/BucksUbp",
            displayInLayerSwitcher: true,
            displayInLegend: false,
            visibleOnFirstLoad: false,
            queryable: false,
            info_format: "text/html",
            layers: [
                {
                    layerName: "7",
                    minZoom: 1,
                    maxZoom: 19
                }
            ]
        }
    }  

    private Monument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Monuments",
                ref: "Monuments",
                type: BuckinghamshireRecordType.Monument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "MonumentPolygonsWFS",
                        minZoom: 10,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesWFS",
                        minZoom: 10,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 11
                    },
                    {
                        layerName: "MonumentPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 11
                    }
                ]
            }, ...opts
        };
    }

    private Event = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Events",
                ref: "Events",
                type: BuckinghamshireRecordType.Event,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "EventPolygonsWFS",
                        minZoom: 10,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventLinesWFS",
                        minZoom: 10,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 11
                    },
                    {
                        layerName: "EventPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 11
                    }
                ]
            }, ...opts
        };
    }

    private Consultation = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Consultations",
                ref: "Consultations",
                type: BuckinghamshireRecordType.Consultation,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "ConsultationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "ConsultationLinesWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "ConsultationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "ConsultationLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    },
                    {
                        layerName: "ConsultationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }

    private ListedBuilding = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Listed Buildings",
                ref: "ListedBuildings",
                type: BuckinghamshireRecordType.Designation,
                subType: BuckinghamshireRecordSubType.ListedBuilding,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: BuckinghamshireRecordSubType.ListedBuilding },
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: false,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }

    private LocallyListedBuilding = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Local Heritage List",
                ref: "LocallyListedBuildings",
                type: BuckinghamshireRecordType.Designation,
                subType: BuckinghamshireRecordSubType.LocallyListedBuilding,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: BuckinghamshireRecordSubType.LocallyListedBuilding },
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: false,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }

    private ScheduledMonument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Scheduled Monuments",
                ref: "ScheduledMonuments",
                type: BuckinghamshireRecordType.Designation,
                subType: BuckinghamshireRecordSubType.ScheduledMonument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: BuckinghamshireRecordSubType.ScheduledMonument },
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },                   
                   
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }
 
    private RegisteredParksAndGardens = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Registered Parks and Gardens",
                ref: "RegisteredParksAndGardens",
                type: BuckinghamshireRecordType.Designation,
                subType: BuckinghamshireRecordSubType.RegisteredParksAndGardens,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: BuckinghamshireRecordSubType.RegisteredParksAndGardens },
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },                                     
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }

    private ConservationArea = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Conservation Areas",
                ref: "ConservationArea",
                type: BuckinghamshireRecordType.Designation,
                subType: BuckinghamshireRecordSubType.ConservationArea,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: BuckinghamshireRecordSubType.ConservationArea },
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }

    private ArchaeologicalNotificationAreas = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Archaeological Notification Areas",
                ref: "ArchaeologicalNotificationAreas",
                type: BuckinghamshireRecordType.Designation,
                subType: BuckinghamshireRecordSubType.ArchaeologicalNotificationAreas,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: BuckinghamshireRecordSubType.ArchaeologicalNotificationAreas },
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }

}

